import { parseISO, isValid, differenceInDays, format } from 'date-fns'

/**
 * Sprint Calendar Configuration
 * Sprint 123 starts on February 7, 2023
 * Each sprint is 14 days long
 */
const SPRINT_123_START_DATE = '2023-02-07'
const SPRINT_123_NUMBER = 123
const SPRINT_DURATION_DAYS = 14

/**
 * Get the current sprint number based on today's date
 * Matches backend logic in sprintCalendarService.js
 */
export const getCurrentSprint = (): number => {
  try {
    const today = new Date()
    const todayString = format(today, 'yyyy-MM-dd')

    const inputDate = parseISO(todayString)
    const baseDate = parseISO(SPRINT_123_START_DATE)

    // Calculate days difference from the base sprint start date
    const daysDifference = differenceInDays(inputDate, baseDate)

    // If the date is before Sprint 123 start date, calculate backwards
    if (daysDifference < 0) {
      const sprintsBefore = Math.ceil(Math.abs(daysDifference) / SPRINT_DURATION_DAYS)
      return SPRINT_123_NUMBER - sprintsBefore
    }

    // Calculate how many complete sprints have passed since Sprint 123
    const sprintsAfter = Math.floor(daysDifference / SPRINT_DURATION_DAYS)
    return SPRINT_123_NUMBER + sprintsAfter
  } catch (error) {
    console.error('Error calculating current sprint:', error)
    // Return a reasonable default (current sprint as of implementation)
    return 195
  }
}

/**
 * Centralized function to determine if an initiative is overdue
 * This ensures consistent logic across all components
 * 
 * @param endDate - The end date string (ISO format)
 * @param status - The current status of the initiative
 * @returns true if the initiative is overdue, false otherwise
 */
export const isOverdue = (endDate: string | null, status: string, debugKey?: string): boolean => {
  if (!endDate) {
    if (debugKey) {
      console.log(`🔍 isOverdue [${debugKey}]: false (no endDate)`)
    }
    return false
  }

  try {
    const endDateObj = parseISO(endDate)
    if (!isValid(endDateObj)) {
      if (debugKey) {
        console.log(`🔍 isOverdue [${debugKey}]: false (invalid date: ${endDate})`)
      }
      return false
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0) // Set to start of day for comparison

    // Only consider it overdue if it's past the end date AND not in a completed/verification/paused state
    const isCompleted = status.toLowerCase().includes('done') ||
                       status.toLowerCase().includes('complete') ||
                       status.toLowerCase().includes('closed') ||
                       status.toLowerCase().includes('awaiting verification') ||
                       status.toLowerCase().includes('verification')

    const isPaused = status.toLowerCase().includes('paused')

    const result = endDateObj < today && !isCompleted && !isPaused

    if (debugKey) {
      console.log(`🔍 isOverdue [${debugKey}]:`, {
        endDate,
        endDateObj: endDateObj.toISOString(),
        today: today.toISOString(),
        status,
        isCompleted,
        isPaused,
        isPastEndDate: endDateObj < today,
        result
      })
    }

    return result
  } catch (error) {
    console.error('Error parsing date:', endDate, error)
    return false
  }
}

/**
 * Check if an initiative is paused or on hold
 * 
 * @param status - The current status of the initiative
 * @returns true if the initiative is paused, false otherwise
 */
export const isPaused = (status: string): boolean => {
  if (!status) return false
  return status.toLowerCase().includes('paused') || status.toLowerCase().includes('hold')
}
